class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      if (currentButtonText.match(new RegExp(previousTerm, 'g')).length > 1) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
      (element) => element.offsetParent !== null
    );
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');

    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(' ', '-').toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    if (this.useRootFallback) {
      this.getFallbackResults(searchTerm, queryKey);
      return;
    }

    fetch(`${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&section_id=predictive-search`, {
      signal: this.abortController.signal,
    })
      .then((response) => {
        // ja, ko, zh-TW: Shopify answers 417 "Unsupported buyer locale" (2026-10-21). Use the primary
        // locale's suggestions for the rest of this page view instead of closing an empty box.
        if (response.status === 417 && this.dataset.fallbackUrl) {
          this.useRootFallback = true;
          console.warn('[predictive-search] 417 from ' + routes.predictive_search_url + '; using the primary-locale suggestions');
          this.getFallbackResults(searchTerm, queryKey);
          return null;
        }
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        if (text === null) return;
        const resultsMarkup = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search').innerHTML;
        // Save bandwidth keeping the cache in all instances synced
        this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
          predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
        });
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        if (error?.code === 20) {
          // Code 20 means the call was aborted
          return;
        }
        this.close();
        throw error;
      });
  }

  // The primary-locale suggest endpoint, rendered here: headings and the "search for" line come from this
  // page's locale (data-t-* on <predictive-search>), product links are moved under this locale's root so
  // the shopper stays on her language. Titles are the primary-locale titles (names stay Latin anyway,
  // translation-rules rule 1). If that call fails too, the box still offers "search for <term>" (Enter).
  getFallbackResults(searchTerm, queryKey) {
    const url = `${this.dataset.fallbackUrl}.json?q=${encodeURIComponent(searchTerm)}` +
      '&resources[type]=product&resources[limit]=6&resources[options][unavailable_products]=last';
    fetch(url, { signal: this.abortController.signal })
      .then((response) => {
        if (!response.ok) throw new Error(response.status);
        return response.json();
      })
      .then((data) => this.fallbackMarkup(searchTerm, (data.resources && data.resources.results && data.resources.results.products) || []))
      .catch((error) => {
        if (error?.code === 20) return null;
        console.warn('[predictive-search] primary-locale suggestions failed (' + (error && error.message) + '); showing search-for only');
        return this.fallbackMarkup(searchTerm, []);
      })
      .then((resultsMarkup) => {
        if (!resultsMarkup) return;
        this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
          predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
        });
        this.renderSearchResults(resultsMarkup);
      });
  }

  fallbackMarkup(searchTerm, products) {
    const esc = (s) => String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
    const root = (this.dataset.localeRoot || '/').replace(/\/$/, '');
    const localise = (u) => (root && typeof u === 'string' && u.charAt(0) === '/' && u.indexOf(root + '/') !== 0 ? root + u : u);
    const items = products
      .map((p, i) => {
        const img = p.featured_image && p.featured_image.url;
        return (
          `<li id="predictive-search-option-product-${i + 1}" class="predictive-search__list-item" role="option" aria-selected="false">` +
          `<a href="${esc(localise(p.url))}" class="predictive-search__item predictive-search__item--link-with-thumbnail link link--text" tabindex="-1">` +
          (img ? `<img class="predictive-search__image" src="${esc(img + (img.indexOf('?') === -1 ? '?' : '&') + 'width=150')}" alt="" width="50" height="50">` : '') +
          `<div class="predictive-search__item-content predictive-search__item-content--centered"><p class="predictive-search__item-heading h5">${esc(p.title)}</p></div></a></li>`
        );
      })
      .join('');
    const groups = products.length
      ? '<div id="predictive-search-results-groups-wrapper" class="predictive-search__results-groups-wrapper predictive-search__results-groups-wrapper--no-suggestions">' +
        '<div class="predictive-search__result-group"><div>' +
        `<h2 id="predictive-search-products" class="predictive-search__heading text-body caption-with-letter-spacing">${esc(this.dataset.tProducts)}</h2>` +
        `<ul id="predictive-search-results-products-list" class="predictive-search__results-list list-unstyled" role="group" aria-labelledby="predictive-search-products">${items}</ul>` +
        '</div></div></div>'
      : '';
    const live = products.length
      ? `${this.dataset.tProducts || ''}: ${products.length}`
      : (this.dataset.tNoResults || '').replace('[terms]', searchTerm);
    return (
      `<div id="predictive-search-results" role="listbox">${groups}` +
      '<div id="predictive-search-option-search-keywords" class="predictive-search__search-for-button">' +
      '<button class="predictive-search__item predictive-search__item--term link link--text h5 animate-arrow" tabindex="-1" role="option" aria-selected="false">' +
      `<span data-predictive-search-search-for-text>${esc((this.dataset.tSearchFor || '').replace('[terms]', searchTerm))}</span></button></div>` +
      `<span class="hidden" data-predictive-search-live-region-count-value>${esc(live)}</span></div>`
    );
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.addServiceLink();
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.open();
  }

  // A service question ("shipping", "送料 日本", "stockists", "受注生産") gets one row at the top linking to the page
  // that answers it (assets/clair-search-assist.js; targets in data-svc-* from snippets/clair-search-service-attrs.liquid).
  // Added at render time, so the cached markup stays Shopify's own. 2026-10-22.
  addServiceLink() {
    const assist = window.ClairSearchAssist;
    const box = this.predictiveSearchResults.querySelector('#predictive-search-results');
    if (!assist || !assist.serviceLink || !box) return;
    const link = assist.serviceLink(this.searchTerm || '', this.dataset);
    if (!link) return;
    const a = document.createElement('a');
    a.className = 'predictive-search__item link link--text h5 animate-arrow';
    a.tabIndex = -1;
    a.href = link.url;
    a.textContent = link.title + ' →';
    const li = document.createElement('li');
    li.id = 'predictive-search-option-service';
    li.className = 'predictive-search__list-item';
    li.setAttribute('role', 'option');
    li.setAttribute('aria-selected', 'false');
    li.appendChild(a);
    const ul = document.createElement('ul');
    ul.className = 'predictive-search__results-list list-unstyled';
    ul.setAttribute('role', 'group');
    ul.appendChild(li);
    box.insertBefore(ul, box.firstChild);
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight - document.querySelector('.section-header').getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
  }
}

customElements.define('predictive-search', PredictiveSearch);
